import produce from 'immer';

import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {TableOpFunction, TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';

/**
 * A utility function to update the BaseStructuredTableMetadata on a FullTableMap, given
 * a targetTableName to make the modification on.
 *
 * @param metadata the BaseStructuredTableMetadata to update the table with
 * @param table the table to update
 */
export function updateStructuredTableMetadataOnTable<TMetadata>(
  metadata: Partial<TMetadata>,
  table: Table
): Table {
  if (table.metadata) {
    return produce(table, (draftTable) => {
      const tableMetadata = draftTable.metadata as StructuredTableToTableMetadata<TMetadata>;
      tableMetadata.metadata = {
        ...tableMetadata.metadata,
        ...metadata,
      };
    });
  }
  return produce(table, (draftTable) => {
    draftTable.metadata = {
      metadata,
    } as StructuredTableToTableMetadata<TMetadata>;
  });
}

/**
 * This is a utility method used to update metadata for structured table.
 *
 * Should not be used if any other changes (e.g. to columns) need to be made.
 */
export default function updateStructuredTableMetadata<TMetadata>({
  performTableOpOnTable,
  metadata,
}: {
  performTableOpOnTable: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;
  metadata: Partial<TMetadata>;
}): void {
  performTableOpOnTable({
    op: (table) => {
      const updatedTable = updateStructuredTableMetadataOnTable(metadata, table);
      return {
        table: updatedTable,
        tokenUpdaters: [],
        box: null,
        dim: null,
        tableOp: {[table.name]: {type: 'noop'}},
      };
    },
  });
}
